<script>
/**
 * AVA-475 — Slice controller (as-built capture, ticket Step 3).
 *
 * RENDERLESS. This component draws nothing. It is the slicing ENGINE only:
 * `selectPlane`/`applySlice` -> `setSlice`, the designKey re-slice watcher, the
 * camera-settle scale probe, and measurement-crop persistence. It used to own a
 * 32 px bar of its own docked above the status bar; that bar was folded into
 * ViewerStatusBar so the viewer has ONE dock, and the two surfaces worth keeping
 * — live mm/px and the refusal notices — now travel there as props by way of
 * WorkspacePanel (`scale-change`, `notice-change`). Leaving the engine in place
 * rather than moving it into either neighbour keeps every watcher, the DRAG_END
 * listener and the `$refs.sliceController.selectPlane` entry point exactly as
 * they were stabilised, so the consolidation stayed a UI-only change.
 *
 * Selecting the first measurement view creates a fixed local box at the ACTIVE
 * POINT'S DESIGN COORDINATE. Later planes rotate only the camera around that
 * same content; `3D` restores the support's crop.
 *
 * WHY THE FRAMING AND THE SCALE PROBE BELONG WITH THE SLICE
 * The live spike measured pick accuracy as a function of zoom with the same
 * +/-3 px hand wobble:
 *     whole-building view (13.9 mm/px) -> 145 mm scatter
 *     300 mm framed view  ( 0.4 mm/px) ->   4 mm scatter
 * Tolerances here are +/-3 mm to +/-60 mm, so an un-zoomed pick is ~50x the
 * tolerance — the UI would report confident nonsense. Slicing therefore also
 * FRAMES (constraint 1), and `scale-change` publishes live mm/px so the status
 * bar can warn when the view is too coarse to assess the active tolerance
 * (constraint 2). The local crop is a correctness mechanism too: on a silhouette
 * edge, picks can otherwise hit a far surface behind the intended one. A 200 mm
 * box isolates the measurement neighbourhood while remaining stable across
 * views. Its size is now fixed: resizing is done with the crop-box drag handles,
 * whose bounds are persisted per point and restored on the next 2D view.
 *
 * UNITS — the trap this feature keeps re-learning. THREE SPACES meet here, and
 * this file talks to all three:
 *
 *   VIEWER units  Positions: design, as-built, crop box, cloud picks, and every
 *                 value handed to `setSlice`. Metres on some facilities,
 *                 millimetres on others — the model decides, never this file.
 *   MILLIMETRES   Human-facing magnitudes: `depthMm`, tolerances, `mmPerPx`.
 *   METRES        Physical constants written into this source, i.e.
 *                 `FRAME_MARGIN_M`. A metre is a metre; a viewer unit is not.
 *
 * So every crossing multiplies or divides by the resolved `unitTransform`, and
 * NOTHING here assumes a viewer unit is a metre — that assumption is what made
 * a 200 mm slice 0.2 mm thick on the millimetre facility. The transform is read
 * from the store (`GET_UNIT_TRANSFORM`), never from the live viewer; while it is
 * null this component refuses to slice or measure rather than guess a scale.
 * See `utils/modelUnits.js` for the whole contract.
 */
import { mapGetters } from 'vuex';

import {
  PLANE_3D,
  PLANE_AXES,
  AXIS_ORDER,
  CANONICAL_VIEW_BY_PLANE,
  capturePlaneForView,
} from '@/features/Verification/utils/asBuiltDraft';
import { isZoomAdequate } from '@/features/Verification/utils/cloudPick';
import {
  boxToBounds,
  setSlice,
  clearSlice,
} from '@/features/Verification/utils/sliceController';
import { assertViewerPoint } from '@/features/Verification/utils/modelUnits';
import { EventBus } from '@/event-bus';
import { CROP_BOX_EVENTS } from '@/utils/cropBoxEvents';
import { pointerToRaycaster } from '@/utils/forgeUtils';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * Measurement-crop edge, millimetres. Fixed rather than typed: the surveyor
 * resizes with the crop-box drag handles, whose bounds persist across views.
 */
const DEFAULT_DEPTH_MM = 200;

/** Screen span used to probe world-per-pixel. Matches cloudPick's own probe. */
const PROBE_SPAN_PX = 100;

/**
 * How far outside the support's crop box a design point may sit and still be
 * believable, in METRES. The crop box is ~1.7 m; a survey-frame design point
 * lands hundreds of metres away, so 1 m separates "near the support" from
 * "different coordinate frame" by three orders of magnitude.
 *
 * A REAL METRE, converted to viewer units before use. Applied raw it would be
 * one viewer unit — a single millimetre of slack on a millimetre facility,
 * which rejects a design point 2 mm off its own support and refuses to slice
 * with a "check its coordinate frame" notice pointing at nothing.
 */
const FRAME_MARGIN_M = 1;

/** Camera-settle debounce before re-measuring the scale, milliseconds. */
const SCALE_SETTLE_MS = 200;

const SCALE_STATE = Object.freeze({
  UNKNOWN: 'unknown',
  NO_TOLERANCE: 'no-tolerance',
  ADEQUATE: 'adequate',
  COARSE: 'coarse',
});

const NOTICE = Object.freeze({
  NO_VIEWER: 'Viewer not ready',
  NO_DESIGN: 'Set a design point before slicing',
  NO_CROP_BOX: 'Support geometry not resolved yet',
  // Distinct from NO_CROP_BOX and from FRAME_MISMATCH on purpose: the geometry
  // can resolve while the model still cannot state its units, and telling a
  // surveyor their coordinate frame is wrong when the real answer is "we do not
  // know how big a unit is yet" sends them to re-check a correct design point.
  NO_UNITS: 'Model units not resolved yet',
  FRAME_MISMATCH: 'Design point is far outside the support — check its coordinate frame',
  SLICE_FAILED: 'Slice could not be applied',
});

export default {
  name: 'SliceController',

  /**
   * A renderless component's root is a comment node, and Vue 2 applies inherited
   * attributes to the root element. Nothing is passed down today, but the
   * failure mode is a mount error rather than a stray attribute, so it is shut
   * off at the source.
   */
  inheritAttrs: false,

  props: {
    /** Controlled by VERIFICATION.activeCapturePlane in WorkspacePanel. */
    activePlane: { type: String, default: PLANE_3D },
  },

  data() {
    return {
      depthMm: DEFAULT_DEPTH_MM,
      /** Design/depth identity for the fixed crop currently in the extension. */
      measurementVolumeKey: null,
      /** Worst-axis screen scale, millimetres per pixel. Null = not measured. */
      mmPerPx: null,
      notice: '',
    };
  },

  computed: {
    ...mapGetters('VERIFICATION', {
      activePoint: 'GET_ACTIVE_POINT',
      cropBox: 'GET_VERIFIED_CROP_BOX',
      viewerStatus: 'GET_VIEWER_STATUS',
      activeMeasurementCrop: 'GET_ACTIVE_MEASUREMENT_CROP',
      // Read only to keep an already-chosen view when a plane is re-applied
      // without one — see `resolveViewId`. The plane, not this, stays canonical.
      activeView: 'GET_ACTIVE_VIEW',
      // How big a viewer unit is. Null until the model resolves; see the header.
      unitTransform: 'GET_UNIT_TRANSFORM',
    }),

    /**
     * Millimetres per viewer unit, or null while unresolved. Every millimetre
     * value this file produces or consumes crosses here.
     */
    mmPerViewerUnit() {
      return this.unitTransform?.mmPerViewerUnit ?? null;
    },

    /** Viewer units in one real metre — for the metre constants in this file. */
    viewerUnitsPerMetre() {
      const scale = this.unitTransform?.siteScale;
      return Number.isFinite(scale) && scale > 0 ? 1 / scale : null;
    },

    /** The Forge viewer lives in ROOT state. */
    viewer() {
      return this.$store.state.viewer;
    },

    viewerReady() {
      return this.viewerStatus === 'ready' || this.viewerStatus === 'isolating';
    },

    /** Design coordinate of the active point, in VIEWER-WORLD METRES. */
    designPoint() {
      return this.activePoint?.design || null;
    },

    /** Changes whenever the point OR its design coordinate changes. */
    designKey() {
      const p = this.designPoint;
      const id = this.activePoint?.id || '';
      return p ? `${id}|${p.x}|${p.y}|${p.z}` : `${id}|none`;
    },

    /**
     * Does the design point plausibly belong to the same coordinate frame as
     * the loaded support? A survey-grid value typed into the design modal sits
     * hundreds of metres from the model, and slicing there shows an empty
     * viewport with no explanation.
     */
    designInSupport() {
      const p = this.designPoint;
      const box = this.cropBox;
      if (!p || !box?.min || !box?.max) return false;
      // Dev-only. This computed already answers "is the point near the box?",
      // so a wrong-space point lands here as a FRAME_MISMATCH refusal with no
      // hint that the frame is fine and only the unit is wrong. The warning
      // names the real cause; the refusal below still stands either way.
      assertViewerPoint(p, box);
      // Fail CLOSED without a scale: the point and box are viewer units, so the
      // margin has to be too, and there is no honest way to size it yet.
      // `applySlice` reports that case as NO_UNITS before it ever gets here.
      const margin = this.viewerUnitsPerMetre === null
        ? null
        : FRAME_MARGIN_M * this.viewerUnitsPerMetre;
      if (margin === null) return false;
      return AXIS_ORDER.every(
        (axis) => p[axis] >= box.min[axis] - margin
          && p[axis] <= box.max[axis] + margin,
      );
    },

    /**
     * The tightest tolerance the CURRENT view is being asked to assess, in
     * millimetres. Only the axes a slice plane can actually resolve count; the
     * 3D tab is judged against all three.
     */
    planeToleranceMm() {
      const tolerance = this.activePoint?.tolerance;
      if (!tolerance) return null;
      const axes = PLANE_AXES[this.activePlane] || AXIS_ORDER;
      const values = axes
        .map((axis) => tolerance[axis])
        .filter((value) => typeof value === 'number' && value > 0);
      return values.length ? Math.min(...values) : null;
    },

    /**
     * Three distinct states, deliberately not two: "not measured yet" must not
     * render as "too coarse", or the bar cries wolf on a conformance tool.
     */
    scale() {
      const mm = this.mmPerPx;
      if (mm === null) {
        return { state: SCALE_STATE.UNKNOWN, value: '—', note: '' };
      }
      const value = `${mm < 1 ? mm.toFixed(2) : mm.toFixed(1)} mm/px`;
      const tolerance = this.planeToleranceMm;
      if (!tolerance) {
        return { state: SCALE_STATE.NO_TOLERANCE, value, note: 'no tolerance set' };
      }
      if (isZoomAdequate({ horiz: mm, vert: mm }, tolerance)) {
        return { state: SCALE_STATE.ADEQUATE, value, note: `ok for ±${tolerance} mm` };
      }
      return {
        state: SCALE_STATE.COARSE,
        value,
        note: `view too coarse to assess ±${tolerance} mm — zoom in`,
      };
    },
  },

  watch: {
    /**
     * Rebuild the crop when the surveyor switches point, or edits the design
     * coordinate of the point being measured. A box left at the old centre is
     * a wrong measurement that still looks right.
     */
    designKey(nextKey, previousKey) {
      const previousPointId = this.pointIdFromDesignKey(previousKey);
      const nextPointId = this.pointIdFromDesignKey(nextKey);
      const pointChanged = !!previousPointId && previousPointId !== nextPointId;

      if (pointChanged && this.activePlane !== PLANE_3D) {
        this.persistMeasurementCrop(previousPointId, previousKey);
      } else if (!pointChanged && nextPointId) {
        this.clearPersistedMeasurementCrop(nextPointId);
      }
      this.measurementVolumeKey = null;
      if (this.activePlane === PLANE_3D) {
        // Point/design changes are real crop boundaries. Unlike a view change,
        // they must not leave the previous point's measurement volume active.
        clearSlice(this.viewer, this.cropBox || null);
        return;
      }
      debugLog('[SliceTabs] design point changed — re-slicing', {
        plane: this.activePlane,
        designKey: this.designKey,
      });
      if (this.selectPlane(this.activePlane, {
        captureIntent: false,
        source: pointChanged ? 'point-change' : 'design-change',
      })) return;

      // INVARIANT: a lit slice tab means a crop IS centred at the current design
      // point. A refusal on the re-slice path would otherwise leave the old
      // tab lit over an unsliced view, which reads as "applied, with a warning".
      debugWarn('[SliceTabs] re-slice refused — falling back to 3D', this.notice);
      const refusal = this.notice;
      this.selectPlane(PLANE_3D, {
        captureIntent: false,
        source: 'design-change-fallback',
      });
      this.notice = refusal;
    },

    viewerReady(ready) {
      if (ready) this.attachCameraListener();
    },

    /**
     * The scale resolves asynchronously, AFTER `mounted` has already probed and
     * been refused. Without this the bar reads '—' until the surveyor happens to
     * move the camera — a stale pending state on the one control that tells them
     * whether their view can even resolve the tolerance.
     */
    mmPerViewerUnit(next) {
      if (next) this.recomputeScreenScale('unit transform resolved');
    },

    /**
     * SINGLE writer of `notice-change`, on the same principle as the `scale`
     * watcher below. Watching the field rather than emitting beside each of the
     * seven assignments keeps refusal reporting out of the engine's control
     * flow, and it collapses the fallback path above — which clears the notice
     * and immediately rewrites it — into one emit describing where the engine
     * actually ended up, rather than a clear the status bar would flicker on.
     */
    notice: {
      immediate: true,
      handler(next) {
        this.$emit('notice-change', next);
      },
    },

    /**
     * SINGLE writer of `scale-change`. Watching the whole computed (not just
     * `.state`) matters: an edited tolerance that keeps the same state still
     * changes the payload, and a consumer left holding the old `toleranceMm`
     * would gate picks against a tolerance nobody chose.
     */
    scale: {
      immediate: true,
      handler(next) {
        if (next.state === SCALE_STATE.COARSE) {
          debugWarn('[SliceTabs] view too coarse for tolerance', {
            mmPerPx: this.mmPerPx,
            toleranceMm: this.planeToleranceMm,
            plane: this.activePlane,
          });
        }
        this.$emit('scale-change', {
          mmPerPx: this.mmPerPx,
          toleranceMm: this.planeToleranceMm,
          adequate: next.state === SCALE_STATE.ADEQUATE,
          state: next.state,
        });
      },
    },
  },

  created() {
    // Instance properties, not methods — a debounced method loses its identity
    // across instances and cannot be cancelled on destroy.
    this._settleTimer = null;
    this._cameraHandler = () => {
      if (this._settleTimer) clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => {
        this._settleTimer = null;
        this.recomputeScreenScale('camera settled');
      }, SCALE_SETTLE_MS);
    };
    this._cameraAttachedTo = null;
    this._onCropBoxDragEnd = ({ viewer } = {}) => {
      if (viewer === this.viewer) this.persistMeasurementCrop();
    };
    EventBus.$on(CROP_BOX_EVENTS.DRAG_END, this._onCropBoxDragEnd);
  },

  mounted() {
    this.attachCameraListener();
    this.recomputeScreenScale('mounted');
  },

  beforeDestroy() {
    if (this._settleTimer) clearTimeout(this._settleTimer);
    this._settleTimer = null;
    this.detachCameraListener();
    EventBus.$off(CROP_BOX_EVENTS.DRAG_END, this._onCropBoxDragEnd);
    this._onCropBoxDragEnd = null;
  },

  methods: {
    pointIdFromDesignKey(key) {
      const separator = String(key || '').indexOf('|');
      return separator > 0 ? String(key).slice(0, separator) : '';
    },

    clearPersistedMeasurementCrop(pointId = this.activePoint?.id) {
      if (!pointId) return;
      this.$store.commit('VERIFICATION/CLEAR_MEASUREMENT_CROP_BOUNDS', pointId);
    },

    /** The crop box currently live in the viewer — null when uncropped. */
    liveCropBox() {
      return this.viewer?.getExtension?.('Cropping3DExtension')?.cropBox || null;
    },

    /**
     * Snapshot the live crop as plain world-space bounds. Handle drags save at
     * drag-end, before any view or lifecycle boundary can replace the live box.
     * The saved bounds survive a temporary switch to the support crop in 3D.
     */
    persistMeasurementCrop(
      pointId = this.activePoint?.id,
      designKey = this.designKey,
    ) {
      if (!pointId || !this.measurementVolumeKey || !this.viewer) return false;
      const cropExt = this.viewer.getExtension?.('Cropping3DExtension');
      const bounds = boxToBounds(cropExt?.cropBox);
      if (!bounds) return false;
      this.$store.commit('VERIFICATION/SET_MEASUREMENT_CROP_BOUNDS', {
        pointId,
        designKey,
        depthMm: this.depthMm,
        bounds,
      });
      return true;
    },

    /**
     * Which named view a plane change should pose the camera to.
     *
     * Three sources, in falling order of authority:
     *   1. the view the surveyor just clicked, when there was one;
     *   2. the LIVE view, when it already captures this plane. This is what
     *      stops a re-slice (the design point moved, a recapture was requested)
     *      from snapping a surveyor standing at `south` back round to `north`.
     *      The camera would end up mirrored down the same axis: still square to
     *      the right plane, so nothing looks wrong, but every subsequent pick is
     *      taken from the opposite side of the feature;
     *   3. the pair's canonical member, when neither of the above applies.
     */
    resolveViewId(plane, viewId) {
      if (viewId && capturePlaneForView(viewId) === plane) return viewId;
      if (capturePlaneForView(this.activeView) === plane) return this.activeView;
      return CANONICAL_VIEW_BY_PLANE[plane] || null;
    },

    /**
     * Apply a slice or change camera orientation around the live crop.
     * Refuses rather than slicing into empty space — see `designInSupport`.
     *
     * `viewId` names WHICH member of the plane's opposite pair to look from.
     * Omit it only when no view was chosen; `resolveViewId` then decides.
     *
     * @returns {boolean} true when the plane is now backed by a real view state
     */
    selectPlane(plane, { captureIntent = true, source = 'slice', viewId = null } = {}) {
      const resolvedViewId = this.resolveViewId(plane, viewId);
      debugLog('[SliceTabs] plane selected', {
        plane,
        viewId: resolvedViewId,
        requestedViewId: viewId,
        depthMm: this.depthMm,
        captureIntent,
        source,
      });
      this.notice = '';

      const viewer = this.viewer;
      if (!viewer) {
        this.notice = NOTICE.NO_VIEWER;
        debugWarn('[SliceTabs] no viewer — plane ignored', plane);
        return false;
      }

      if (plane === PLANE_3D) {
        this.persistMeasurementCrop();
        // Isometric deliberately shows the original support crop. Keep the
        // saved measurement bounds in Vuex, but clear the live-volume identity
        // so the next 2D view restores those exact edited bounds.
        clearSlice(viewer, this.cropBox || null);
        this.measurementVolumeKey = null;
        debugLog('[SliceTabs] 3D view selected, support crop restored', {
          hasSavedMeasurementCrop: !!this.activeMeasurementCrop,
        });
        this.setActivePlane(plane, {
          captureIntent: false, source, viewId: resolvedViewId,
        });
        return true;
      }

      // View/Slice synchronization starts in Design, before there is a point
      // around which a real measurement crop can be built. The plane is still
      // valid NAVIGATION: it updates the canonical plane and asks the status
      // bar to apply the matching camera pose, but never arms capture or claims
      // that a slice exists. Once Design is saved, the designKey watcher calls
      // this method again and upgrades the same active plane to a real crop.
      const navigationOnly = !this.designPoint;
      if (navigationOnly) {
        // Pure navigation must not touch the surveyor's crop: keep whatever box
        // is live, including manual handle adjustments. Only a point/design
        // context change falls back to the support crop — the 3D plane (handled
        // above) stays the one deliberate reset to the original crop.
        const contextReset = source === 'design-change' || source === 'point-change';
        const preservedBox = contextReset ? null : this.liveCropBox();
        clearSlice(viewer, preservedBox || this.cropBox || null);
        debugLog('[SliceTabs] navigation-only plane change', {
          plane,
          source,
          cropPreserved: !!preservedBox,
        });
        this.setActivePlane(plane, {
          captureIntent: false,
          source,
          navigationOnly,
          viewId: resolvedViewId,
        });
        return true;
      }

      const explicitReset = source === 'design-change';
      if (explicitReset) this.clearPersistedMeasurementCrop();
      const stored = explicitReset ? null : this.activeMeasurementCrop;
      const canRestore = !this.measurementVolumeKey
        && stored?.designKey === this.designKey
        && Number(stored?.depthMm) === Number(this.depthMm)
        && stored?.bounds;
      const resetVolume = explicitReset || (!this.measurementVolumeKey && !canRestore);
      if (!this.applySlice(plane, {
        resetVolume,
        savedBounds: canRestore ? stored.bounds : null,
        viewId: resolvedViewId,
      })) return false;
      this.setActivePlane(plane, {
        captureIntent, source, navigationOnly: false, viewId: resolvedViewId,
      });
      return true;
    },

    /**
     * Apply a named camera view around one fixed measurement crop.
     * @returns {boolean} true when the volume/view was applied
     */
    applySlice(plane, { resetVolume = false, savedBounds = null, viewId = null } = {}) {
      const center = this.designPoint;
      if (!center) {
        this.notice = NOTICE.NO_DESIGN;
        debugWarn('[SliceTabs] no design point to slice at', plane);
        return false;
      }
      if (!this.cropBox) {
        this.notice = NOTICE.NO_CROP_BOX;
        debugWarn('[SliceTabs] no crop box — support geometry unresolved');
        return false;
      }
      // BEFORE the frame check, which now depends on the same transform: with no
      // scale that check fails closed, and reporting a units-pending state as a
      // coordinate-frame mismatch would send the surveyor after the wrong bug.
      if (!this.mmPerViewerUnit) {
        this.notice = NOTICE.NO_UNITS;
        debugWarn('[SliceTabs] no unit transform — refusing to slice', { plane });
        return false;
      }
      if (!this.designInSupport) {
        this.notice = NOTICE.FRAME_MISMATCH;
        debugWarn('[SliceTabs] design point outside the support — refusing to slice', {
          design: center,
          cropMin: this.cropBox.min,
          cropMax: this.cropBox.max,
        });
        return false;
      }

      // THE unit boundary: the crop size is millimetres, setSlice wants VIEWER
      // units — the same space as `baseBox` and `center`, whatever the model
      // calls them.
      const applied = setSlice(this.viewer, {
        plane,
        center,
        depth: this.depthMm / this.mmPerViewerUnit,
        baseBox: this.cropBox,
        preserveCrop: !resetVolume && !!this.measurementVolumeKey,
        savedBounds,
        viewId,
      });
      if (!applied) {
        this.notice = NOTICE.SLICE_FAILED;
        debugWarn('[SliceTabs] setSlice refused', { plane, depthMm: this.depthMm });
        return false;
      }
      debugLog('[SliceTabs] slice applied', {
        plane,
        viewId,
        depthMm: this.depthMm,
        depthViewer: this.depthMm / this.mmPerViewerUnit,
        mmPerViewerUnit: this.mmPerViewerUnit,
        center,
        cropPreserved: !resetVolume && !!this.measurementVolumeKey,
      });
      this.measurementVolumeKey = `${this.designKey}|${this.depthMm}`;
      this.persistMeasurementCrop();
      return true;
    },

    setActivePlane(
      plane,
      { captureIntent = true, source = 'slice', navigationOnly = false, viewId = null } = {},
    ) {
      // `viewId` rides the payload so WorkspacePanel can label and pose the view
      // the surveyor is ACTUALLY looking from. Deriving it from the plane there
      // would collapse an opposite pair onto its canonical member — the combo
      // would snap from "Northings · South" back to "Northings · North" the
      // instant the slice landed.
      this.$emit('plane-change', {
        plane, captureIntent, source, navigationOnly, viewId,
      });
      // The camera move fires CAMERA_CHANGE_EVENT, so the scale re-measures
      // itself once the transition lands; this is the immediate first read.
      this.recomputeScreenScale(`plane ${plane}`);
    },

    /**
     * Millimetres of world per client pixel, by unprojecting two screen points
     * PROBE_SPAN_PX apart. Geometry-free — unlike a cloud-probe it cannot fail
     * because the probe fell off the cloud, and it works with a tight crop.
     *
     * The two projections need different maths and unifying them would be
     * quietly wrong:
     *   ORTHOGRAPHIC — the rays are parallel, so the world span is exactly the
     *     distance between the two ray ORIGINS, at any depth.
     *   PERSPECTIVE  — the rays share an origin, so the span is the divergence
     *     of their DIRECTIONS scaled by the depth of the point of interest.
     * @returns {number|null} worst of horizontal/vertical, or null if unmeasurable
     */
    measureMmPerPx() {
      const viewer = this.viewer;
      const canvas = viewer?.canvas;
      const camera = viewer?.impl?.camera;
      if (!canvas || !camera) return null;
      // No scale, no reading: the unprojected span is in viewer units, and
      // "unknown" must render as '—' rather than as a number a thousand times
      // off that would clear or trip the too-coarse gate on its own.
      if (!this.mmPerViewerUnit) return null;

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      const clientX = rect.left + rect.width / 2;
      const clientY = rect.top + rect.height / 2;

      // Depth reference for the perspective case: what the surveyor is aiming at.
      // ONE SPACE, both branches: the design point and the crop-box centre are
      // both VIEWER units, which is also what the ray origins below are in.
      // Keep it that way — a conversion on either branch would silently scale
      // the depth term in `perspectiveSpan` and nothing on screen would show it.
      const target = this.designPoint || this.boxCentre(this.cropBox);
      if (camera.isPerspective && !target) return null;

      try {
        const origin = pointerToRaycaster(viewer, { clientX, clientY });
        const across = pointerToRaycaster(viewer, { clientX: clientX + PROBE_SPAN_PX, clientY });
        const down = pointerToRaycaster(viewer, { clientX, clientY: clientY + PROBE_SPAN_PX });
        if (!origin?.ray || !across?.ray || !down?.ray) return null;

        const span = camera.isPerspective
          ? (a, b) => this.perspectiveSpan(a, b, target)
          : (a, b) => this.distance(a.ray.origin, b.ray.origin);

        const horiz = span(origin, across);
        const vert = span(origin, down);
        const worst = Math.max(horiz, vert);
        if (!Number.isFinite(worst) || worst <= 0) return null;
        return (worst * this.mmPerViewerUnit) / PROBE_SPAN_PX;
      } catch (err) {
        debugError('[SliceTabs] screen-scale probe failed', err);
        return null;
      }
    },

    /**
     * World gap between two shared-origin rays at the depth of `target`.
     * Depth is the AXIAL distance (target projected onto the view ray), not the
     * straight-line distance: an off-centre target would otherwise overstate the
     * depth and so overstate mm/px, warning "too coarse" on an adequate view.
     */
    perspectiveSpan(rayA, rayB, target) {
      const o = rayA.ray.origin;
      const a = rayA.ray.direction;
      const depth = (target.x - o.x) * a.x + (target.y - o.y) * a.y + (target.z - o.z) * a.z;
      if (!(depth > 0)) return NaN; // target behind the camera — unmeasurable
      const b = rayB.ray.direction;
      return this.distance(
        { x: a.x * depth, y: a.y * depth, z: a.z * depth },
        { x: b.x * depth, y: b.y * depth, z: b.z * depth },
      );
    },

    distance(a, b) {
      return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    },

    boxCentre(box) {
      if (!box?.min || !box?.max) return null;
      return {
        x: (box.min.x + box.max.x) / 2,
        y: (box.min.y + box.max.y) / 2,
        z: (box.min.z + box.max.z) / 2,
      };
    },

    // Writes state only. `scale-change` is emitted by the `scale` watcher, so
    // there is exactly one writer of that event.
    recomputeScreenScale(reason) {
      const next = this.measureMmPerPx();
      if (next === this.mmPerPx) return;
      this.mmPerPx = next;
      debugLog('[SliceTabs] screen scale recomputed', {
        reason,
        mmPerPx: next,
        plane: this.activePlane,
      });
    },

    attachCameraListener() {
      const viewer = this.viewer;
      const eventName = window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT;
      if (!viewer || !eventName || typeof viewer.addEventListener !== 'function') return;
      if (this._cameraAttachedTo === viewer) return;

      this.detachCameraListener();
      viewer.addEventListener(eventName, this._cameraHandler);
      this._cameraAttachedTo = viewer;
      debugLog('[SliceTabs] camera listener attached');
    },

    detachCameraListener() {
      const viewer = this._cameraAttachedTo;
      const eventName = window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT;
      if (viewer && eventName && typeof viewer.removeEventListener === 'function') {
        viewer.removeEventListener(eventName, this._cameraHandler);
        debugLog('[SliceTabs] camera listener detached');
      }
      this._cameraAttachedTo = null;
    },
  },

  render: () => null,
};
</script>
